import { useEffect, useState, useRef } from "react";
import GitFitGridLoader from "@/components/GitFitGridLoader";
import { format } from "date-fns";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { captureException, addBreadcrumb } from "@/lib/sentry";
import { signOut } from "@/lib/auth";
import { useUserCredits } from "@/hooks/useUserCredits";
import { useProfile } from "@/hooks/useProfile";
import { useAppSession } from "@/providers/AppSessionProvider";
import { useEffectiveUserId } from "@/hooks/useEffectiveUserId";
import { useTheme } from "@/providers/ThemeProvider";
import { safeNavigate } from "@/lib/navigationGuard";
import DailyEntryForm from "@/components/DailyEntryForm";
import ContributionChart from "@/components/ContributionChart";
import { QuickEntryInput } from "@/components/QuickEntryInput";
import { QuickMetricButtons } from "@/components/QuickMetricButtons";
import { DataInsightsInput } from "@/components/DataInsightsInput";
import { CustomViewsGrid } from "@/components/CustomViewsGrid";
import Leaderboard from "@/components/Leaderboard";
import FriendsList from "@/components/FriendsList";
import AccountSettings from "@/components/AccountSettings";
import { NotificationSettings } from "@/components/NotificationSettings";
import PendingRequestsBadge from "@/components/PendingRequestsBadge";
import { ReferralDialog } from "@/components/ReferralDialog";
import { AvatarUpload } from "@/components/AvatarUpload";
import { AppLogo } from "@/components/AppLogo";
import { SubscriptionGate } from "@/components/SubscriptionGate";
import { FollowersFollowingDialog } from "@/components/FollowersFollowingDialog";
import { InstallAppButton } from "@/components/InstallAppButton";
import { openInExternalBrowser, detectNativeApp } from "@/lib/platform";
import { getLandingVariant } from "@/lib/abTest";
import { trackOnboardingEvent } from "@/lib/onboardingAnalytics";
import {
  Activity,
  Eye,
  EyeOff,
  Users,
  Trophy,
  Medal,
  Award,
  Copy,
  ChevronRight,
  Settings,
  MapPin,
  AlertTriangle,
  CalendarIcon,
  Sparkles,
  LogOut,
  Lock,
  Moon,
  Dices,
} from "lucide-react";
import MobileBottomNav from "@/components/MobileBottomNav";
import { FooterLinksMenu } from "@/components/FooterLinksMenu";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn, formatNumber, formatCurrency, getLocalizedMetricName } from "@/lib/utils";
import { toast } from "sonner";
import type { User } from "@supabase/supabase-js";
import { TutorialStep } from "@/components/auth/onboarding/TutorialStep";
import { MetricsTour } from "@/components/auth/mobile/MetricsTour";
import { ENABLE_COMMITMENT_PLEDGES } from "@/config/features";
import { CustomMetricsTrialWarning } from "@/components/CustomMetricsTrialWarning";
import { useCustomMetricsCleanup } from "@/hooks/useCustomMetricsCleanup";
import { useBackfillCheck } from "@/hooks/useBackfillCheck";
import { BackfillPrompt } from "@/components/BackfillPrompt";
import { useBackfillChecklistStore } from "@/stores/backfillChecklistStore";
import { useTranslation } from "react-i18next";
import { isDemoAccount } from "@/lib/screenshotDataGenerator";

const Index = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { toggleTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [metricsWarningOpen, setMetricsWarningOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [metricsTourOpen, setMetricsTourOpen] = useState(false);
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const [showVisibilityDialog, setShowVisibilityDialog] = useState(false);
  const [avatarReady, setAvatarReady] = useState(false);
  const [forceShowPage, setForceShowPage] = useState(false);
  const [followersDialogOpen, setFollowersDialogOpen] = useState(false);
  const [followersDialogTab, setFollowersDialogTab] = useState<"followers" | "following">("followers");
  const [homeViewMode, setHomeViewMode] = useState<'minimal' | 'profile' | 'progress'>(() => {
    const stored = localStorage.getItem('gitfit-home-view-mode');
    return (stored as 'minimal' | 'profile' | 'progress') || 'profile';
  });
  const [showDiceButtons, setShowDiceButtons] = useState(() => {
    return localStorage.getItem('gitfit-screenshot-mode') === 'true';
  });
  const [backfillOpen, setBackfillOpen] = useState(false);
  
  // Persist screenshot mode to localStorage
  useEffect(() => {
    localStorage.setItem('gitfit-screenshot-mode', showDiceButtons ? 'true' : 'false');
  }, [showDiceButtons]);
  
  const isMobile = useIsMobile();

  // Force re-check mobile state after any window resize or visibility change
  // This ensures layout is correct after app restart
  useEffect(() => {
    const forceLayoutCheck = () => {
      // Trigger a re-render by dispatching a resize event
      window.dispatchEvent(new Event('resize'));
    };

    // Check on visibility change (e.g., app coming back from background)
    document.addEventListener('visibilitychange', forceLayoutCheck);

    // Check after a short delay on mount (for app restart scenarios)
    const timeout = setTimeout(forceLayoutCheck, 100);

    return () => {
      document.removeEventListener('visibilitychange', forceLayoutCheck);
      clearTimeout(timeout);
    };
  }, []);

  // Use centralized session management
  const { ready, session, isAuthenticated } = useAppSession();
  const effectiveUserId = useEffectiveUserId();

  // Auto-cleanup expired custom metrics for non-premium users
  useCustomMetricsCleanup(effectiveUserId);

  // Check for unlogged gap days and prompt user to backfill or mark as no data
  const { data: backfillGaps } = useBackfillCheck(effectiveUserId);

  useEffect(() => {
    // Wait for session to be ready before making routing decisions
    if (ready !== 'ready') {
      console.log('[Index] Session not ready yet, waiting...');
      setLoading(true);
      return;
    }

    // Once ready, check authentication
    if (!isAuthenticated) {
      console.log('[Index] No authenticated session - redirecting...');
      // A/B test: randomly assign landing variant (50/50 showcase vs onboarding)
      const variant = getLandingVariant();
      console.log(`[Index] A/B variant assigned: ${variant}`);
      // Track the variant assignment
      trackOnboardingEvent({ eventType: "landing_variant_assigned", metadata: { variant } });

      detectNativeApp().then((detection) => {
        // Always redirect to /onboarding (A/B showcase test disabled for now)
        console.log(`[Index] Redirecting to /onboarding (native=${detection.isNative}, mobile=${isMobile})`);
        safeNavigate(() => navigate('/onboarding', { replace: true }));
      });
      return;
    }

    // If authenticated, we're good
    console.log('[Index] Session ready and authenticated');
    setLoading(false);
  }, [ready, isAuthenticated, navigate, isMobile]);

  // Force show page after 2 seconds to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      setForceShowPage(true);
    }, 2000);
    return () => clearTimeout(timeout);
  }, []);

  // Show HealthKit notification if user selected HealthKit metrics during onboarding
  useEffect(() => {
    if (!isAuthenticated || !ready) return;

    // Check for pending HealthKit metrics
    const getPendingHealthKitMetrics = () => {
      try {
        const stored = localStorage.getItem("gitfit_healthkit_metrics_pending");
        if (!stored) return [];
        return JSON.parse(stored);
      } catch (error) {
        console.error("[Index] Failed to get pending HealthKit metrics:", error);
        return [];
      }
    };

    const clearPendingHealthKitMetrics = () => {
      try {
        localStorage.removeItem("gitfit_healthkit_metrics_pending");
      } catch (error) {
        console.error("[Index] Failed to clear pending HealthKit metrics:", error);
      }
    };

    const healthKitMetrics = getPendingHealthKitMetrics();
    if (healthKitMetrics.length > 0) {
      console.log("[Index] Found pending HealthKit metrics:", healthKitMetrics);

      // Small delay to ensure dashboard is loaded before showing toast
      const timeout = setTimeout(() => {
        const metricsText = healthKitMetrics.length === 1
          ? healthKitMetrics[0]
          : healthKitMetrics.length === 2
            ? `${healthKitMetrics[0]} and ${healthKitMetrics[1]}`
            : `${healthKitMetrics.slice(0, -1).join(", ")}, and ${healthKitMetrics[healthKitMetrics.length - 1]}`;

        toast.info(t('dashboard.healthkit_sync_info', { metrics: metricsText }), {
          duration: 8000,
          action: {
            label: t('dashboard.open_settings'),
            onClick: () => navigate("/settings"),
          },
        });

        clearPendingHealthKitMetrics();
      }, 1500);

      return () => clearTimeout(timeout);
    }
  }, [isAuthenticated, ready, navigate]);

  // Handle tutorial trigger from username setup
  useEffect(() => {
    if (location.state?.startTutorial) {
      // On mobile, navigate to full page tutorial instead of dialog
      if (isMobile) {
        navigate("/tutorial", { replace: true });
      } else {
        setTutorialOpen(true);
        // Clean up state so refresh doesn't retrigger
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, isMobile, navigate]);

  console.log('[Index] effectiveUserId:', effectiveUserId);

  const { data: profile, isLoading: isLoadingProfile } = useProfile(effectiveUserId);
  const { data: userCredits, isLoading: isLoadingCredits } = useUserCredits(effectiveUserId);

  console.log('[Index] Profile query result:', { profile, isLoadingProfile });

  // Listen for subscription sync events and immediately refetch credits
  useEffect(() => {
    const handleSubscriptionSync = (event: CustomEvent) => {
      if (event.detail.userId === effectiveUserId) {
        console.log("[Index] Subscription synced, refetching credits immediately", {
          userId: event.detail.userId,
          isPremium: event.detail.isPremium
        });
        // Force immediate refetch - invalidateQueries marks as stale AND refetches
        queryClient.invalidateQueries({ queryKey: ["user-credits", effectiveUserId] });
        queryClient.invalidateQueries({ queryKey: ["subscription-status", effectiveUserId] });
        queryClient.invalidateQueries({ queryKey: ["profile", effectiveUserId] });
      }
    };

    window.addEventListener("subscription-synced" as any, handleSubscriptionSync);
    return () => {
      window.removeEventListener("subscription-synced" as any, handleSubscriptionSync);
    };
  }, [effectiveUserId, queryClient]);

  // Check for URL params (e.g., returning from tutorial page)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // Only show upsell if user is not already premium
    if (params.get("showUpsell") === "true" && !profile?.is_premium) {
      navigate("/upgrade", { replace: true });
    }
  }, [location.search, navigate, profile]);

  // Trigger subscription sync when landing on homepage (e.g., after returning from checkout)
  useEffect(() => {
    if (ready === 'ready' && isAuthenticated && effectiveUserId) {
      // Small delay to ensure SubscriptionSync component is mounted
      const timeout = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("force-subscription-sync"));
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [ready, isAuthenticated, effectiveUserId]);

  // NOTE: Subscription checking removed - now handled by global scheduled job
  // Subscription status is synced every minute automatically
  // Users can read is_premium directly from the profiles table

  // Separate effect to handle username check - redirect if no username
  useEffect(() => {
    if (ready !== 'ready' || !isAuthenticated || isLoadingProfile) return;

    // IMPORTANT: Only redirect if we have a loaded profile that definitely has no username
    // If profile is undefined or null, it might just be loading/refetching - don't redirect
    if (profile && profile.username === null) {
      console.log("[Index] No username found (username is null) - checking if just completed setup");

      // Check if user just completed username setup - give profile time to refetch
      const justCompletedSetup = sessionStorage.getItem('just_completed_username_setup');
      if (justCompletedSetup) {
        console.log("[Index] User just completed username setup, allowing time for profile to refetch");
        sessionStorage.removeItem('just_completed_username_setup');
        // Force refetch profile
        queryClient.invalidateQueries({ queryKey: ["profile", effectiveUserId] });
        return;
      }

      console.log("[Index] Redirecting to auth for onboarding");
      // Redirect to auth page which will show the OnboardingFlow component
      safeNavigate(() => navigate("/auth", { replace: true }));
    } else if (profile?.username) {
      console.log("[Index] Username exists:", profile.username, "- staying on dashboard");
    }
  }, [ready, isAuthenticated, profile, isLoadingProfile, navigate, effectiveUserId, queryClient]);

  // Preload avatar to prevent white flash (with timeout to prevent blocking)
  useEffect(() => {
    if (profile?.avatar_url) {
      const img = new Image();
      img.onload = () => setAvatarReady(true);
      img.onerror = () => setAvatarReady(true); // Still show page if image fails
      img.src = profile.avatar_url;

      // If already cached, mark as ready immediately
      if (img.complete) {
        setAvatarReady(true);
      }

      // Timeout: don't block page load for more than 500ms
      const timeout = setTimeout(() => {
        setAvatarReady(true);
      }, 500);

      return () => clearTimeout(timeout);
    } else {
      // No avatar, ready to show
      setAvatarReady(true);
    }
  }, [profile?.avatar_url]);

  // Sync HealthKit data on dashboard visit (only once per session)
  useEffect(() => {
    const syncHealthKit = async () => {
      if (!effectiveUserId) return;

      // Check if we've already synced HealthKit in this session
      const hasAlreadySynced = sessionStorage.getItem('healthkit_synced_this_session');
      if (hasAlreadySynced) {
        console.log("[Dashboard] HealthKit already synced this session, skipping");
        return;
      }

      try {
        // Check if running in native app first
        const detection = await detectNativeApp();
        if (!detection.isNative) {
          console.log("[Dashboard] Not in native app, skipping HealthKit sync");
          return;
        }

        // Check if user has HealthKit enabled
        const { data: settings } = await supabase
          .from("healthkit_settings")
          .select("enabled")
          .eq("user_id", effectiveUserId)
          .maybeSingle();

        if (!settings?.enabled) return;

        console.log("[Dashboard] HealthKit enabled, syncing today's data...");
        toast.info(t('dashboard.syncing_healthkit'), { duration: 3000 });

        // Dynamically import to avoid loading SDK on non-iOS platforms
        const { syncTodayHealthKitData } = await import("@/lib/healthkit");
        const success = await syncTodayHealthKitData(effectiveUserId);

        // Mark as synced for this session
        sessionStorage.setItem('healthkit_synced_this_session', 'true');

        // Get detailed sync results
        const syncResults = (window as any).__healthKitLastSyncResults as Array<{
          metric: string;
          success: boolean;
          error?: string;
          value?: any;
        }> || [];

        if (success) {
          console.log("[Dashboard] HealthKit sync completed");

          const failedMetrics = syncResults.filter(r => !r.success);
          const successMetrics = syncResults.filter(r => r.success);

          if (failedMetrics.length > 0) {
            const failedNames = failedMetrics.map(m => `${m.metric}: ${m.error}`).join(', ');
            if (successMetrics.length > 0) {
              const successDetails = successMetrics.map(m => {
                const value = m.value;
                if (m.metric === 'Steps') return `${m.metric}: ${value} steps`;
                if (m.metric === 'Sleep Time') return `${m.metric}: ${value.toFixed(1)} hrs`;
                return `${m.metric}: ${value}`;
              }).join(', ');
              toast.warning(`Synced: ${successDetails}. Failed: ${failedNames}`, { duration: 8000 });
            } else {
              toast.error(t('dashboard.healthkit_sync_failed', { details: failedNames }), { duration: 8000 });
            }
          } else if (successMetrics.length > 0) {
            const successDetails = successMetrics.map(m => {
              const value = m.value;
              if (m.metric === 'Steps') return `${m.metric}: ${value} steps`;
              if (m.metric === 'Sleep Time') return `${m.metric}: ${value.toFixed(1)} hrs`;
              return `${m.metric}: ${value}`;
            }).join(', ');
            toast.success(t('dashboard.healthkit_sync_success', { details: successDetails }), { duration: 8000 });
          }

          // Invalidate queries to refresh the dashboard
          queryClient.invalidateQueries({ queryKey: ["daily-entry"] });
          queryClient.invalidateQueries({ queryKey: ["daily-entries"] });
        } else {
          console.log("[Dashboard] HealthKit sync failed");
          const syncError = (window as any).__healthKitSyncError;
          const failedMetrics = syncResults.filter(r => !r.success);

          let errorMessage = "Failed to sync HealthKit data";

          if (syncError) {
            errorMessage = `Sync Error: ${syncError}`;
          } else if (failedMetrics.length > 0) {
            const details = failedMetrics.map(m => `${m.metric}: ${m.error}`).join(', ');
            errorMessage = `Failed: ${details}`;
          }

          toast.error(errorMessage, { duration: 8000 });
        }
      } catch (e) {
        console.error("[Dashboard] Failed to sync HealthKit:", e);
        captureException(e, {
          tags: { page: 'index', action: 'healthkit-sync' },
          extra: { userId: session?.user?.id }
        });
        toast.error(t('dashboard.healthkit_sync_error', { error: e instanceof Error ? e.message : 'Unknown error' }), { duration: 8000 });
      }
    };

    syncHealthKit();
  }, [session?.user?.id, queryClient]);

  // Sync GitHub commits on dashboard visit
  useEffect(() => {
    const syncGitHubCommits = async () => {
      if (!effectiveUserId) return;

      try {
        // Check if user has GitHub connected
        const { data: connection } = await supabase
          .from("github_connections")
          .select("last_synced_at")
          .eq("user_id", session.user.id)
          .maybeSingle();

        if (!connection) return;

        console.log("GitHub connected, syncing commits...", connection);

        // Determine sync range based on last sync
        const lastSynced = connection.last_synced_at ? new Date(connection.last_synced_at) : null;
        const today = new Date();
        const todayStr = today.toISOString().split("T")[0];

        // If synced within last 5 minutes, skip (backend likely just did a full sync)
        if (lastSynced) {
          const timeSinceSync = Date.now() - lastSynced.getTime();
          const minutesSinceSync = timeSinceSync / (1000 * 60);

          if (minutesSinceSync < 5) {
            console.log(`Last synced ${Math.round(minutesSinceSync)} minutes ago, skipping auto-sync`);
            return;
          }
        }

        let startDate: string;
        if (lastSynced) {
          const lastSyncDate = new Date(lastSynced);
          const lastSyncStr = lastSyncDate.toISOString().split("T")[0];

          // If last sync was today, re-sync today (in case of new commits)
          // Otherwise, sync from the day after last sync to today
          if (lastSyncStr === todayStr) {
            startDate = todayStr;
          } else {
            lastSyncDate.setDate(lastSyncDate.getDate() + 1);
            startDate = lastSyncDate.toISOString().split("T")[0];

            // Ensure startDate doesn't go into the future
            if (startDate > todayStr) {
              startDate = todayStr;
            }
          }
        } else {
          // First time - sync 365 days
          const oneYearAgo = new Date(today);
          oneYearAgo.setDate(today.getDate() - 365);
          startDate = oneYearAgo.toISOString().split("T")[0];
          console.log("First time sync: fetching 365 days of commits");
        }

        const endDate = todayStr;

        console.log(`Syncing commits from ${startDate} to ${endDate}`);

        // Always sync - even if same day, in case of new commits
        const { data: sessionData } = await supabase.auth.getSession();
        const { data, error } = await supabase.functions.invoke("sync-github-commits", {
          headers: { Authorization: `Bearer ${sessionData.session?.access_token}` },
          body: { userId: effectiveUserId, startDate, endDate }
        });

        if (error) {
          console.error("Sync error:", error);
        } else {
          console.log("Sync completed:", data);
        }

        // Invalidate queries to refresh the chart
        queryClient.invalidateQueries({ queryKey: ["daily-entry"] });
      } catch (e) {
        console.error("Failed to sync GitHub commits:", e);
        captureException(e, {
          tags: { page: 'index', action: 'github-sync' },
          extra: { userId: session?.user?.id }
        });
      }
    };

    syncGitHubCommits();
  }, [session?.user?.id, queryClient]);

  // Show backfill prompt when gap days are detected (after a short delay to avoid being intrusive)
  useEffect(() => {
    if (!backfillGaps || backfillGaps.length === 0) return;
    // Don't show if already dismissed this session
    if (sessionStorage.getItem("backfill_dismissed")) return;
    // Don't show if already shown this session
    if (sessionStorage.getItem("backfill_shown")) return;

    // Delay to let the dashboard fully load before showing the prompt
    const timeout = setTimeout(() => {
      sessionStorage.setItem("backfill_shown", "true");
      setBackfillOpen(true);
    }, 2500);

    return () => clearTimeout(timeout);
  }, [backfillGaps]);

  // Derive premium status from profile (source of truth for subscriptions)
  // Don't default to false - pass undefined while loading so QuickEntryInput can show loading state
  const effectiveIsPremium = profile?.is_premium || profile?.is_lifetime_premium;

  // Debug logging for premium status
  console.log('[Index] Premium status derived:', {
    profileIsPremium: profile?.is_premium,
    effectiveIsPremium,
    effectiveIsPremiumType: typeof effectiveIsPremium,
    profileLoaded: !!profile,
    isLoadingProfile
  });

  // Fetch metric preferences to check if user has metrics enabled
  const { data: metricPrefs } = useQuery({
    queryKey: ["metric-preferences", effectiveUserId],
    queryFn: async () => {
      if (!effectiveUserId) return null;
      const { data, error } = await supabase
        .from("metric_preferences")
        .select("*")
        .eq("user_id", effectiveUserId)
        .maybeSingle();
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
    enabled: !!effectiveUserId,
  });

  // Fetch custom metrics to check if user has any
  const { data: customMetrics } = useQuery({
    queryKey: ["custom-metrics", effectiveUserId],
    queryFn: async () => {
      if (!effectiveUserId) return [];
      const { data, error } = await supabase
        .from("custom_metrics")
        .select("*")
        .eq("user_id", effectiveUserId);
      if (error) throw error;
      return data || [];
    },
    enabled: !!effectiveUserId,
  });

  // Check if user has any metrics enabled (built-in or custom)
  const hasNoBuiltInMetrics = !metricPrefs || (
    (!metricPrefs.nutrition_metrics || metricPrefs.nutrition_metrics.length === 0) &&
    (!metricPrefs.activity_sleep_metrics || metricPrefs.activity_sleep_metrics.length === 0) &&
    (!metricPrefs.results_metrics || metricPrefs.results_metrics.length === 0)
  );
  const hasNoMetrics = hasNoBuiltInMetrics && (!customMetrics || customMetrics.length === 0);

  // Check if user has ever used the AI input feature
  const hasUsedAI = localStorage.getItem('ai_input_used') === 'true';

  // Show "Start here" tooltip if user hasn't used AI input yet
  const shouldShowTryFirstBubble = !hasUsedAI;

  // Redirect mobile users to /track page on app open if they have metrics enabled
  useEffect(() => {
    if (ready !== 'ready' || !isMobile || !effectiveUserId || !profile) return;

    // Only check once per app session using sessionStorage
    const hasCheckedThisSession = sessionStorage.getItem(`track_redirect_checked_${effectiveUserId}`);
    if (hasCheckedThisSession) return;

    // Mark as checked for this session
    sessionStorage.setItem(`track_redirect_checked_${effectiveUserId}`, "true");

    // If user has any metrics enabled (hasNoMetrics is false), redirect to track page
    if (!hasNoMetrics) {
      console.log("[Index] Mobile user with metrics enabled, redirecting to /track");
      navigate("/track", { replace: true });
    }
  }, [ready, isMobile, effectiveUserId, profile, hasNoMetrics, navigate]);

  // Fetch followers count (excluding blocked and deleted users)
  const { data: followersCount = 0 } = useQuery({
    queryKey: ["followers-count", effectiveUserId],
    queryFn: async () => {
      if (!effectiveUserId) return 0;

      // Get all follower IDs
      const { data: followRows, error: followErr } = await supabase
        .from("follows")
        .select("follower_id")
        .eq("following_id", effectiveUserId);

      if (followErr) throw followErr;
      if (!followRows?.length) return 0;

      // Get blocked user IDs
      const { data: { user } } = await supabase.auth.getUser();
      let blockedUserIds: string[] = [];
      if (user) {
        const { data: usersIBlocked } = await supabase
          .from("blocked_users")
          .select("blocked_user_id")
          .eq("user_id", user.id);

        const { data: usersWhoBlockedMe } = await supabase
          .from("blocked_users")
          .select("user_id")
          .eq("blocked_user_id", user.id);

        const blockedByMe = usersIBlocked?.map(b => b.blocked_user_id) || [];
        const blockedMe = usersWhoBlockedMe?.map(b => b.user_id) || [];
        blockedUserIds = [...new Set([...blockedByMe, ...blockedMe])];
      }

      // Filter out blocked users
      const filtered = followRows.filter((f: any) => !blockedUserIds.includes(f.follower_id));
      if (!filtered.length) return 0;

      // Filter out soft-deleted users
      const userIds = filtered.map((f: any) => f.follower_id);
      const { data: activeUserIds } = await (supabase as any).rpc('get_active_user_ids', {
        user_ids: userIds
      });

      return (activeUserIds as string[])?.length || 0;
    },
    enabled: !!effectiveUserId,
    staleTime: 5 * 60 * 1000, // Keep fresh for 5 minutes
    placeholderData: (previousData) => previousData, // Keep showing old count while refetching
  });

  // Fetch following count (excluding blocked and deleted users)
  const { data: followingCount = 0 } = useQuery({
    queryKey: ["following-count", effectiveUserId],
    queryFn: async () => {
      if (!effectiveUserId) return 0;

      // Get all following IDs
      const { data: followRows, error: followErr } = await supabase
        .from("follows")
        .select("following_id")
        .eq("follower_id", effectiveUserId);

      if (followErr) throw followErr;
      if (!followRows?.length) return 0;

      // Get blocked user IDs
      const { data: { user } } = await supabase.auth.getUser();
      let blockedUserIds: string[] = [];
      if (user) {
        const { data: usersIBlocked } = await supabase
          .from("blocked_users")
          .select("blocked_user_id")
          .eq("user_id", user.id);

        const { data: usersWhoBlockedMe } = await supabase
          .from("blocked_users")
          .select("user_id")
          .eq("blocked_user_id", user.id);

        const blockedByMe = usersIBlocked?.map(b => b.blocked_user_id) || [];
        const blockedMe = usersWhoBlockedMe?.map(b => b.user_id) || [];
        blockedUserIds = [...new Set([...blockedByMe, ...blockedMe])];
      }

      // Filter out blocked users
      const filtered = followRows.filter((f: any) => !blockedUserIds.includes(f.following_id));
      if (!filtered.length) return 0;

      // Filter out soft-deleted users
      const userIds = filtered.map((f: any) => f.following_id);
      const { data: activeUserIds } = await (supabase as any).rpc('get_active_user_ids', {
        user_ids: userIds
      });

      return (activeUserIds as string[])?.length || 0;
    },
    enabled: !!effectiveUserId,
    staleTime: 5 * 60 * 1000, // Keep fresh for 5 minutes
    placeholderData: (previousData) => previousData, // Keep showing old count while refetching
  });

  // Check for custom metrics overflow on non-premium accounts
  const { data: customMetricsCheck } = useQuery({
    queryKey: ["custom-metrics-check", effectiveUserId, profile?.is_premium, profile?.created_at],
    queryFn: async () => {
      if (!effectiveUserId || !profile?.created_at) return null;

      // Check if user is in trial period (first 7 days)
      const daysSinceCreation = Math.floor(
        (Date.now() - new Date(profile.created_at).getTime()) / (1000 * 60 * 60 * 24)
      );
      const isInTrialPeriod = daysSinceCreation < 7;

      // Don't check if user is premium or in trial period
      if (profile?.is_premium || isInTrialPeriod) {
        return null;
      }

      const { data, error } = await supabase.from("custom_metrics").select("id").eq("user_id", session.user.id);
      if (error) throw error;
      return data?.length || 0;
    },
    enabled: !!session?.user && !!profile && profile?.is_premium === false,
  });

  // Show warning dialog if non-premium user has more than 3 custom metrics (and not in trial)
  useEffect(() => {
    if (customMetricsCheck && customMetricsCheck > 3) {
      setMetricsWarningOpen(true);
    }
  }, [customMetricsCheck]);

  // Get current user's rank if they have a public profile
  const { data: userRank, isLoading: isLoadingUserRank } = useQuery({
    queryKey: ["user-rank-header", effectiveUserId],
    queryFn: async () => {
      if (!effectiveUserId) return null;

      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("is_public, is_premium")
        .eq("id", effectiveUserId)
        .single();

      if (profileError || !profileData?.is_public || !profileData?.is_premium) return null;

      // Get rank from all leaderboards
      const leaderboards = [
        { view: "leaderboard_calories", emoji: "🔥", label: "Calories", key: "calories" },
        { view: "leaderboard_protein", emoji: "🍗", label: "Protein", key: "protein" },
        { view: "leaderboard_miles", emoji: "🏃", label: "Run Distance", key: "miles_ran" },
        { view: "leaderboard_fiber", emoji: "🥦", label: "Fiber", key: "fiber" },
        { view: "leaderboard_water", emoji: "💧", label: "Water", key: "water_gallons" },
        { view: "leaderboard_gym_time", emoji: "💪", label: "Gym Time", key: "gym_time" },
        { view: "leaderboard_github_commits", emoji: "💻", label: "GitHub", key: "github_commits" },
        { view: "leaderboard_body_fat", emoji: "📊", label: "Body Fat", key: "body_fat_percentage" },
        { view: "leaderboard_ate_healthy", emoji: "🥗", label: "Ate Healthy", key: "ate_healthy" },
        { view: "leaderboard_low_sugars", emoji: "🚫", label: "Low Sugars", key: "low_sugars" },
        { view: "leaderboard_fasted", emoji: "🍽️", label: "Fasted", key: "fasted" },
        { view: "leaderboard_donations", emoji: "💝", label: "Donations", key: "donations" },
      ];

      const firstPlaceRanks: Array<{ emoji: string; label: string; key: string }> = [];
      let bestNonFirstRank = null;
      let bestNonFirstMetric: { emoji: string; label: string; key: string } | null = null;

      for (const lb of leaderboards) {
        const { data } = await supabase
          .from(lb.view as any)
          .select("*")
          .eq("id", session.user.id)
          .maybeSingle();

        if (data) {
          const leaderData: any = data;
          if (leaderData.rank) {
            const rank = Number(leaderData.rank);
            if (rank === 1) {
              firstPlaceRanks.push({ emoji: lb.emoji, label: lb.label, key: lb.key });
            } else if (!bestNonFirstRank || rank < bestNonFirstRank) {
              bestNonFirstRank = rank;
              bestNonFirstMetric = { emoji: lb.emoji, label: lb.label, key: lb.key };
            }
          }
        }
      }

      // Prioritize showing #1 ranks
      if (firstPlaceRanks.length > 0) {
        return { rank: 1, metrics: firstPlaceRanks };
      } else if (bestNonFirstRank) {
        return { rank: bestNonFirstRank, metrics: [bestNonFirstMetric] };
      }

      return null;
    },
    enabled: !!session?.user,
  });

  const getRankDisplay = (rankData: { rank: number; metrics: Array<{ emoji: string; label: string; key: string }> } | null) => {
    if (!rankData || !rankData.metrics || rankData.metrics.length === 0) return null;

    const { rank, metrics } = rankData;
    let icon = null;
    if (rank === 1) icon = <Trophy className="w-4 h-4 text-yellow-500" />;
    else if (rank === 2) icon = <Medal className="w-4 h-4 text-gray-400" />;
    else if (rank === 3) icon = <Award className="w-4 h-4 text-amber-600" />;

    // For multiple categories
    if (metrics.length > 1) {
      return (
        <button onClick={() => navigate("/social")} className="flex items-center gap-1 hover:underline cursor-pointer">
          {icon}
          <span>
            {t('profile.worldwide_categories', { rank, count: metrics.length })}
          </span>
        </button>
      );
    }

    // Single category display - use translated label
    const metric = metrics[0];
    const translatedLabel = getLocalizedMetricName(metric.key, t, metric.label);
    if (icon) {
      return (
        <div className="flex items-center gap-1">
          {icon}
          <span>
            {t('profile.worldwide', { rank, emoji: metric.emoji, label: translatedLabel })}
          </span>
        </div>
      );
    }
    return (
      <span>
        {t('profile.worldwide', { rank, emoji: metric.emoji, label: translatedLabel })}
      </span>
    );
  };

  const updateVisibility = useMutation({
    mutationFn: async (isPublic: boolean) => {
      if (!session?.user) throw new Error("User not found");
      const { error } = await supabase.from("profiles_internal").update({ is_public: isPublic }).eq("id", session.user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", effectiveUserId] });
      queryClient.invalidateQueries({ queryKey: ["profile-settings", effectiveUserId] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-profile"] });
      toast.success(t('notifications.toast.profile_visibility_updated'));
      setShowVisibilityDialog(false);
    },
    onError: () => {
      toast.error(t('notifications.toast.failed_to_update_visibility'));
    },
  });

  const deleteExtraMetrics = useMutation({
    mutationFn: async () => {
      if (!session?.user?.id) throw new Error("User not found");

      // Get all custom metrics for this user
      const { data: metrics } = await supabase
        .from("custom_metrics")
        .select("id, created_at")
        .eq("user_id", session.user.id)
        .order("created_at", { ascending: true });

      if (!metrics || metrics.length <= 1) return;

      // Keep only the first one, delete the rest
      const toDelete = metrics.slice(1).map((m) => m.id);

      const { error } = await supabase.from("custom_metrics").delete().in("id", toDelete);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["custom-metrics"] });
      queryClient.invalidateQueries({ queryKey: ["custom-metrics-check"] });
      toast.success(t('notifications.toast.extra_custom_metrics_removed'));
      setMetricsWarningOpen(false);
    },
    onError: () => {
      toast.error(t('notifications.toast.failed_to_remove_extra_metrics'));
    },
  });

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  const handleSignOutClick = () => {
    if (isMobile) {
      setShowSignOutDialog(true);
    } else {
      handleSignOut();
    }
  };

  // Cycle through view modes: minimal -> profile -> progress -> minimal
  const cycleViewMode = () => {
    const modes: Array<'minimal' | 'profile' | 'progress'> = ['minimal', 'profile', 'progress'];
    const currentIndex = modes.indexOf(homeViewMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    const nextMode = modes[nextIndex];
    setHomeViewMode(nextMode);
    localStorage.setItem('gitfit-home-view-mode', nextMode);
  };

  const getViewModeIcon = () => {
    switch (homeViewMode) {
      case 'minimal':
        return <EyeOff className="w-4 h-4" />;
      case 'profile':
        return <Users className="w-4 h-4" />;
      case 'progress':
        return <Activity className="w-4 h-4" />;
    }
  };

  const shareProfile = () => {
    if (displayProfile.username) {
      const shareUrl = `${window.location.origin}/${displayProfile.username}`;
      navigator.clipboard.writeText(shareUrl);
      toast.success(t('notifications.toast.profile_link_copied'));

      // Show visibility dialog if the account is private
      if (!displayProfile.is_public) {
        setShowVisibilityDialog(true);
      }
    }
  };

  // Only show loading screen on initial auth load, not profile/avatar refetches
  // Force show page after 2 seconds to prevent getting stuck
  // BUT: Always wait for user session to load, even after timeout
  // SPECIAL: If restart_required param, show restart message instead of loading
  const params = new URLSearchParams(location.search);
  const restartRequired = params.get("restart_required") === "true";

  if (restartRequired) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="mb-4 text-4xl">🎉</div>
          <h2 className="text-2xl font-bold mb-2">{t('ui.text.welcome_to_pro')}</h2>
          <p className="text-muted-foreground mb-6">{t('ui.text.please_close_and_restart_the_app_to_access_your_su')}</p>
          <p className="text-sm text-muted-foreground/70">{t('ui.text.on_ios_swipe_up_to_close_the_app_then_open_it_agai')}</p>
        </div>
      </div>
    );
  }

  // Show loading while session is being checked or if not authenticated
  if (ready !== 'ready' || loading || !isAuthenticated || (!forceShowPage && (isLoadingProfile && !profile))) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center relative overflow-hidden">
        {/* Subtle ambient glow behind loader */}
        <div
          className="absolute w-[240px] h-[240px] rounded-full opacity-[0.04] animate-pulse"
          style={{
            background: 'radial-gradient(circle, hsl(var(--contrib-3)) 0%, transparent 70%)',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
        <GitFitGridLoader size={64} className="mx-auto relative z-10" />
      </div>
    );
  }

  // If force showing but no profile yet, show loading state in the UI instead of blocking
  const displayProfile = profile || {
    username: session?.user?.email?.split("@")[0] || "User",
    is_public: false,
    donations_total: 0,
    avatar_url: null,
    profile_views: 0,
  };

  // Log profile state for debugging
  if (!profile) {
    console.warn('[Index] Profile data is missing - using fallback display profile', {
      effectiveUserId,
      isLoadingProfile,
      hasSession: !!session,
    });
  } else if (!profile.username) {
    console.warn('[Index] Profile loaded but username is missing', {
      effectiveUserId,
      profileId: profile.id,
      hasAvatar: !!profile.avatar_url,
    });
  } else {
    console.log('[Index] Profile loaded successfully', {
      username: profile.username,
      hasAvatar: !!profile.avatar_url,
      isPremium: profile.is_premium,
    });
  }

  return (
    <div className="min-h-screen bg-background pb-32 md:pb-0">
      {/* Subscription gate - blocks non-premium signed-in users */}
      <SubscriptionGate open={!isLoadingProfile && !!profile && !effectiveIsPremium} />

      <div className="container max-w-6xl mx-auto py-8 px-4">
        <header className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <AppLogo isLoading={isLoadingProfile} />
            <div className="flex items-center gap-3">
              {isMobile ? (
                <>
                  <FooterLinksMenu onOpenMetricsTour={() => setMetricsTourOpen(true)} onSignOut={handleSignOutClick} />
                  <Button variant="ghost" size="sm" onClick={toggleTheme} title={t('accessibility.labels.toggle_theme')} className="active:bg-transparent">
                    <Moon className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={cycleViewMode} title={t('view_modes.switch_view')} className="active:bg-transparent">
                    {getViewModeIcon()}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" size="icon" onClick={() => navigate("/social")} className="relative">
                    <Users className="w-5 h-5" />
                    <PendingRequestsBadge userId={session.user.id} />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => navigate("/settings")}>
                    <Settings className="w-5 h-5" />
                  </Button>
                  <button
                    onClick={() => navigate("/credits-success")}
                    className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {isLoadingCredits ? (
                      t('dashboard.loading')
                    ) : (
                      <>
                        {userCredits?.credits !== undefined ? (userCredits.credits / 10).toFixed(1) : "0"} {t('dashboard.credits')}
                        <ChevronRight className="w-3 h-3" />
                      </>
                    )}
                  </button>
                  <ReferralDialog userId={session.user.id} />
                  <Button variant="ghost" size="icon" onClick={toggleTheme} title={t('dashboard.toggle_theme')} className="active:bg-transparent">
                    <Moon className="w-5 h-5" />
                  </Button>
                  <Button variant="outline" onClick={handleSignOutClick}>
                    {t('dashboard.sign_out')}
                  </Button>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 text-xs font-normal text-muted-foreground hover:text-foreground"
                      >
                        {format(selectedDate, "EEE d")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-popover z-50" align="end">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => date && setSelectedDate(date)}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                        disabled={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const oneYearAgo = new Date(today);
                          oneYearAgo.setDate(today.getDate() - 365);
                          const oneYearForward = new Date(today);
                          oneYearForward.setDate(today.getDate() + 365);
                          return date < oneYearAgo || date > oneYearForward;
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </>
              )}
            </div>
          </div>

          {/* View Mode: Minimal - show nothing in profile area */}
          {isMobile && homeViewMode === 'minimal' && (
            <div className="w-full mt-2 mb-2" />
          )}

          {/* View Mode: Progress - show quick metric buttons in circular form */}
          {isMobile && homeViewMode === 'progress' && (
            <div className="w-full mt-2 mb-4">
              <div className="flex justify-center">
                <QuickMetricButtons userId={session.user.id} selectedDate={selectedDate} displayMode="circles" username={displayProfile?.username} />
              </div>
            </div>
          )}

          {/* View Mode: Profile - show full profile info (default) */}
          {(!isMobile || homeViewMode === 'profile') && (
          <div className="w-full mt-2">
            <div className="flex items-start gap-4 mb-2">
              <div className="h-20 w-20">
                <AvatarUpload
                  userId={session.user.id}
                  currentAvatarUrl={displayProfile.avatar_url}
                  username={displayProfile.username}
                  size="lg"
                  showUploadButton={false}
                  clickable={true}
                  uploadInputId="dashboard-avatar-upload"
                  onUploadComplete={() => {
                    queryClient.invalidateQueries({ queryKey: ['dashboard-profile'] });
                  }}
                />
              </div>
              <div className="flex flex-col gap-0 min-w-0 flex-1 h-20 justify-center">
                <div className="flex items-baseline gap-2">
                  <h2 className="text-lg font-semibold truncate leading-tight">@{(() => {
                    const displayName = displayProfile.username || session?.user?.email?.split("@")[0] || "User";
                    if (!displayProfile.username) {
                      console.warn('[Index] Displaying fallback username (email prefix) instead of profile username', {
                        displayName,
                        hasProfile: !!profile,
                        profileUsername: profile?.username,
                      });
                    }
                    // Show 'aaron' for demo account
                    if (isDemoAccount(displayProfile.username)) {
                      return 'aaron';
                    }
                    return displayName;
                  })()}</h2>
                  {displayProfile.username && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={shareProfile}
                      className="h-5 w-5 flex-shrink-0 mt-0.5"
                    >
                      <Copy className="w-2 h-2 opacity-50" />
                    </Button>
                  )}
                  {effectiveIsPremium && (
                    <button
                      onClick={() => navigate("/upgrade")}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-500/20 to-accent/20 border border-purple-500/30 flex-shrink-0 hover:from-purple-500/30 hover:to-accent/30 transition-colors cursor-pointer"
                    >
                      <Sparkles className="w-3 h-3 text-purple-500" />
                      <span className="text-xs font-semibold text-purple-500">{t('ui.text.pro')}</span>
                    </button>
                  )}
                </div>
                {displayProfile.username && (
                  <>
                    {/* Only show rank line when profile is public */}
                    {displayProfile.is_public && (isLoadingUserRank || userRank) && (
                      <div className="flex items-center gap-2 min-w-0 mt-[2px]">
                        {isLoadingUserRank && effectiveIsPremium && (
                          <span className="text-xs text-muted-foreground/50 flex items-center gap-1 flex-shrink-0">
                            {t('dashboard.fetching_rank')}<span className="inline-block animate-pulse">...</span>
                          </span>
                        )}
                        {!isLoadingUserRank && userRank && (
                          <span className="text-[11px] text-muted-foreground flex-shrink-0">{getRankDisplay(userRank)}</span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground/60 mt-[8px]">
                      {!displayProfile.is_public && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setShowVisibilityDialog(true)}
                          className="h-4 w-4 flex-shrink-0 hover:bg-transparent p-0 -ml-1"
                          title={t('accessibility.labels.profile_is_private')}
                        >
                          <Lock className="w-3 h-3 text-muted-foreground/50" />
                        </Button>
                      )}
                      <button
                        onClick={() => {
                          setFollowersDialogTab("followers");
                          setFollowersDialogOpen(true);
                        }}
                        className="hover:text-muted-foreground transition-colors"
                      >
                        <span className="font-semibold text-foreground">{formatNumber(followersCount)}</span> {t('common.followers')}
                      </button>
                      <button
                        onClick={() => {
                          setFollowersDialogTab("following");
                          setFollowersDialogOpen(true);
                        }}
                        className="hover:text-muted-foreground transition-colors"
                      >
                        <span className="font-semibold text-foreground">{formatNumber(followingCount)}</span> {t('common.following')}
                      </button>
                      {displayProfile.is_public && displayProfile.profile_views !== undefined && (
                        <div className="inline-flex items-center gap-1 flex-shrink-0">
                          <Eye className="w-3 h-3" />
                          <span className="font-medium">{formatNumber(displayProfile.profile_views)}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  {ENABLE_COMMITMENT_PLEDGES && Number(displayProfile.donations_total) > 0 && (
                    <p className="text-[11px] font-medium text-muted-foreground border border-border/50 rounded-full px-2 py-0.5 bg-background/40">
                      {t('profile.donated', { amount: formatCurrency(Number(displayProfile.donations_total)) })}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {!isMobile && (
              <div className="flex gap-2 items-end w-full mt-8 mb-3 h-[44px] relative">
                <div className="flex-none w-[36%] relative">
                  <div className="mb-1 flex items-center gap-1">
                    <Sparkles className="w-4 h-4 text-green-500 animate-pulse" />
                    <span className="text-sm font-semibold bg-gradient-to-r from-green-500 via-emerald-400 to-green-500 bg-clip-text text-transparent">{t('ui.text.track_with_ai')}</span>
                  </div>
                  <QuickEntryInput userId={session.user.id} isPremium={effectiveIsPremium} selectedDate={selectedDate} showStartHereTooltip={shouldShowTryFirstBubble} />
                </div>
                <div className="ml-auto flex-1 min-w-0 absolute right-0 bottom-0">
                  <QuickMetricButtons userId={session.user.id} selectedDate={selectedDate} username={displayProfile?.username} />
                </div>
              </div>
            )}
          </div>
          )}

          {/* Custom Metrics Trial Warning */}
          <div className="mt-6">
            <CustomMetricsTrialWarning userId={session.user.id} />
          </div>

          {/* Data Insights Input */}
          <div data-component="data-insights-input" className="mt-6 bg-card border border-border rounded-lg rounded-b-none border-b-0" style={{ padding: '12px 18px' }}>
            <DataInsightsInput 
              userId={session.user.id} 
              isPremium={effectiveIsPremium}
              userCredits={userCredits?.credits}
            />
          </div>

          {/* Custom Views Grid – pinned AI-generated views (pro only) */}
          <CustomViewsGrid userId={session.user.id} userCredits={userCredits?.credits} isPremium={effectiveIsPremium} />

          <div className="bg-card border border-border rounded-lg rounded-t-none" style={{ padding: '18px' }}>
            <ContributionChart
              userId={session.user.id}
              chartOnly
              showFilters
              isPremium={effectiveIsPremium}
              onDateSelect={setSelectedDate}
              currentViewerId={session.user.id}
              showDiceButtons={showDiceButtons && isDemoAccount(displayProfile?.username)}
            />
          </div>

          {/* Compare Button - Only show if user has metrics */}
          {!hasNoMetrics && (
            <div className="mt-6 flex flex-col items-center gap-2">
              <Button
                onClick={() => navigate("/compare")}
                variant="ghost"
                size="sm"
                className="gap-2 text-muted-foreground hover:text-foreground"
              >
                <Activity className="w-3.5 h-3.5" />{t('ui.text.compare_graphs')}</Button>
              {isDemoAccount(displayProfile?.username) && (
                <Button
                  onClick={() => setShowDiceButtons(!showDiceButtons)}
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "gap-2",
                    showDiceButtons ? "text-purple-500 hover:text-purple-600" : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Dices className="w-3.5 h-3.5" />
                  {showDiceButtons ? "Screenshot Mode: ON" : "Screenshot Mode"}
                </Button>
              )}
            </div>
          )}
        </header>

        {/* Tutorial Dialog - Desktop Only */}
        {!isMobile && (
          <Dialog open={tutorialOpen} onOpenChange={setTutorialOpen}>
            <DialogContent className="max-w-2xl">
              <TutorialStep onComplete={() => {
                setTutorialOpen(false);
                // Pro upsell will be shown on third AI quick entry button press
              }} />
            </DialogContent>
          </Dialog>
        )}

        {/* Metrics Tour */}
        <MetricsTour
          isOpen={metricsTourOpen}
          onClose={() => setMetricsTourOpen(false)}
          metrics={(() => {
            // Build metrics list from user's enabled metrics
            const tourMetrics: Array<{
              key: string;
              name: string;
              emoji: string;
              unit: string;
              type: "numeric" | "boolean";
            }> = [];

            // Add custom metrics (using correct DB column names)
            if (customMetrics && customMetrics.length > 0) {
              customMetrics.forEach((cm: any) => {
                tourMetrics.push({
                  key: cm.metric_key,
                  name: cm.metric_name,
                  emoji: cm.metric_emoji || "⭐",
                  unit: cm.metric_unit || "",
                  type: cm.metric_type || "numeric",
                });
              });
            }

            // Add built-in metrics from preferences
            if (metricPrefs) {
              const allBuiltInKeys = [
                ...(metricPrefs.nutrition_metrics || []),
                ...(metricPrefs.activity_sleep_metrics || []),
                ...(metricPrefs.results_metrics || []),
              ];

              // Use metric definitions with localized names for the tour
              const builtInMetricInfo: Record<string, { emoji: string; unit: string; type: "numeric" | "boolean" }> = {
                calories: { emoji: "🔥", unit: "kcal", type: "numeric" },
                protein: { emoji: "🍗", unit: "g", type: "numeric" },
                carbs: { emoji: "🍞", unit: "g", type: "numeric" },
                fat: { emoji: "🥑", unit: "g", type: "numeric" },
                fiber: { emoji: "🥦", unit: "g", type: "numeric" },
                added_sugars: { emoji: "🍬", unit: "g", type: "numeric" },
                water_gallons: { emoji: "💧", unit: "gal", type: "numeric" },
                healthkit_steps: { emoji: "👟", unit: "steps", type: "numeric" },
                healthkit_exercise_time: { emoji: "💪", unit: "min", type: "numeric" },
                sleep: { emoji: "😴", unit: "hrs", type: "numeric" },
                body_weight: { emoji: "⚖️", unit: "lbs", type: "numeric" },
                body_fat_percentage: { emoji: "📊", unit: "%", type: "numeric" },
                gym_time: { emoji: "🏋️", unit: "min", type: "numeric" },
                miles_ran: { emoji: "🏃", unit: "mi", type: "numeric" },
                mood: { emoji: "😊", unit: "/10", type: "numeric" },
                self_appearance_rating: { emoji: "🪞", unit: "/10", type: "numeric" },
                github_commits: { emoji: "💻", unit: "commits", type: "numeric" },
                ate_healthy: { emoji: "🥗", unit: "", type: "boolean" },
                low_sugars: { emoji: "🚫", unit: "", type: "boolean" },
                fasted: { emoji: "⏰", unit: "", type: "boolean" },
              };

              allBuiltInKeys.forEach(key => {
                const info = builtInMetricInfo[key];
                if (info) {
                  tourMetrics.push({
                    key,
                    name: getLocalizedMetricName(key, t),
                    ...info,
                  });
                }
              });
            }

            return tourMetrics.length > 0 ? tourMetrics : undefined;
          })()}
        />

        {/* Custom Metrics Warning Dialog */}
        <Dialog open={metricsWarningOpen} onOpenChange={setMetricsWarningOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />{t('ui.text.subscription_ended')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">{t('ui.text.your_pro_subscription_has_ended_custom_metrics_are')}</p>
              <p className="text-sm font-semibold">
                You currently have {customMetricsCheck} custom metrics. They will be deleted unless you upgrade now.
              </p>
            </div>
            <DialogFooter className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => deleteExtraMetrics.mutate()}
                disabled={deleteExtraMetrics.isPending}
              >
                {deleteExtraMetrics.isPending ? "Deleting..." : "Continue & Delete"}
              </Button>
              <Button
                onClick={() => {
                  setMetricsWarningOpen(false);
                  navigate("/upgrade");
                }}
              >{t('ui.text.upgrade_to_keep_metrics')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <MobileBottomNav />

      {/* Sign Out Confirmation Dialog */}
      <AlertDialog open={showSignOutDialog} onOpenChange={setShowSignOutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('dashboard.sign_out')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('dashboard.sign_out_confirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('dashboard.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleSignOut}>{t('dashboard.sign_out')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Profile Visibility Dialog */}
      <Dialog open={showVisibilityDialog} onOpenChange={setShowVisibilityDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('ui.text.make_your_profile_public')}</DialogTitle>
            <DialogDescription>{t('ui.text.your_profile_link_has_been_copied_your_profile_is_')}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center gap-3 p-4 border rounded-lg">
              {displayProfile.is_public ? (
                <>
                  <Eye className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <Label className="font-semibold">{t('ui.text.public_profile')}</Label>
                    <p className="text-sm text-muted-foreground">{t('ui.text.visible_to_everyone')}</p>
                  </div>
                </>
              ) : (
                <>
                  <EyeOff className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <Label className="font-semibold">{t('ui.text.private_profile')}</Label>
                    <p className="text-sm text-muted-foreground">{t('ui.text.only_visible_to_friends')}</p>
                  </div>
                </>
              )}
            </div>
          </div>
          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setShowVisibilityDialog(false)}>{t('ui.text.keep_private')}</Button>
            <Button onClick={() => updateVisibility.mutate(true)} disabled={updateVisibility.isPending}>
              {updateVisibility.isPending ? t('dashboard.updating') : t('dashboard.make_public')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Followers/Following Dialog */}
      <FollowersFollowingDialog
        open={followersDialogOpen}
        onOpenChange={setFollowersDialogOpen}
        profileUserId={session.user.id}
        initialTab={followersDialogTab}
      />

      {/* Backfill Prompt - shown when there are unlogged gap days */}
      {backfillGaps && backfillGaps.length > 0 && (
        <BackfillPrompt
          open={backfillOpen}
          onOpenChange={setBackfillOpen}
          gaps={backfillGaps}
          userId={session.user.id}
          onGoLog={() => {
            // Initialize the backfill checklist overlay with gap data
            if (backfillGaps) {
              useBackfillChecklistStore.getState().initFromGaps(backfillGaps, session.user.id);
            }
            navigate("/track");
          }}
        />
      )}

      <InstallAppButton />
    </div>
  );
};

export default Index;
